import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Check, 
  Crown, 
  Zap, 
  Building2, 
  Users,
  ArrowLeft,
  Sparkles,
  CreditCard,
  QrCode,
  FileText,
  Loader2,
  AlertCircle,
  Copy,
  CheckCircle2,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicPlans = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  const [processing, setProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [companyStatus, setCompanyStatus] = useState(null);
  
  // Check if user is logged in and their subscription status
  const isLoggedIn = !!user && !!token;
  const isTrialExpiredOrBlocked = companyStatus?.is_blocked || 
    companyStatus?.status === 'expired' || 
    companyStatus?.status === 'cancelled' ||
    (companyStatus?.status === 'trial' && companyStatus?.trial_expired);

  // Load plans from API
  const loadPlans = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/asaas/plans`);
      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      // Use fallback plans on error
      setPlans(getDefaultPlans());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Check subscription status if logged in
  const checkSubscriptionStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/asaas/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanyStatus(data);
      }
    } catch (error) {
      // Silent fail - subscription check is optional
    }
  }, [token]);

  useEffect(() => {
    if (isLoggedIn) {
      checkSubscriptionStatus();
    }
  }, [isLoggedIn, checkSubscriptionStatus]);

  const getDefaultPlans = () => [
    {
      id: 'starter',
      name: 'Starter',
      price_monthly: 145,
      price_annual: 1653,  // 145 * 12 * 0.95
      employees_label: 'Até 50 funcionários',
      features: [
        'Até 50 funcionários',
        'Dashboard completo',
        'Gestão de Riscos Ocupacionais',
        'Avaliações Psicossociais',
        'Plano de Ação',
        'Relatórios básicos',
        'Suporte por email'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price_monthly: 870,
      price_annual: 9918,  // 870 * 12 * 0.95
      employees_label: 'Até 300 funcionários',
      features: [
        'Até 300 funcionários',
        'Tudo do Starter',
        'Integração e-Social',
        'Relatórios Avançados',
        'Treinamentos NR-1',
        'Análise de IA',
        'Suporte Prioritário'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price_monthly: 1740,
      price_annual: 19836,  // 1740 * 12 * 0.95
      employees_label: 'Até 600 funcionários',
      features: [
        'Até 600 funcionários',
        'Tudo do Professional',
        'API de Integração',
        'Exportação Excel avançada',
        'Gerente de Conta dedicado',
        'Customizações sob demanda'
      ]
    },
    {
      id: 'master',
      name: 'Master',
      price_monthly: 4220,
      price_annual: 48108,  // 4220 * 12 * 0.95
      employees_label: 'Funcionários Ilimitados',
      multi_cnpj: true,
      features: [
        'Funcionários ILIMITADOS',
        'Multi-CNPJ (várias empresas)',
        'Gestão de múltiplas empresas',
        'Consolidação de relatórios',
        'Dashboard corporativo',
        'API completa',
        'Suporte 24/7 dedicado'
      ]
    }
  ];

  const getIcon = (planId) => {
    const icons = {
      starter: Zap,
      professional: Building2,
      enterprise: Users,
      master: Crown
    };
    return icons[planId] || Zap;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleSelectPlan = (plan) => {
    if (!user || !token) {
      // Not logged in - redirect to register with plan pre-selected
      toast.info('Crie uma conta para contratar o plano');
      navigate('/register', { state: { selectedPlan: plan.id } });
      return;
    }
    
    // User is logged in - go directly to checkout (skip registration)
    setSelectedPlan(plan);
    setShowCheckout(true);
    setPaymentResult(null);
  };

  const handleCheckout = async () => {
    if (!selectedPlan || !token) return;
    
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/asaas/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          billing_type: paymentMethod,
          cycle: billingCycle
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setPaymentResult(data.payment);
        toast.success('Pagamento criado com sucesso!');
      } else {
        toast.error(data.detail || 'Erro ao processar pagamento');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Código copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Erro ao copiar');
    }
  };

  const closeCheckout = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
    setPaymentResult(null);
  };

  const getPrice = (plan) => {
    if (!plan) return 0;
    if (billingCycle === 'annual') {
      return plan.price_annual || 0;
    }
    return plan.price_monthly || 0;
  };

  const getMonthlyPrice = (plan) => {
    if (!plan) return 0;
    if (billingCycle === 'annual') {
      return (plan.price_annual || 0) / 12;
    }
    return plan.price_monthly || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0d9488]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-white" data-testid="public-plans-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#0d9488] text-white py-16 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Button 
            variant="ghost" 
            className="absolute left-4 top-4 text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="h-8 w-8" />
            <h1 className="text-3xl md:text-4xl font-bold">
              {isLoggedIn && isTrialExpiredOrBlocked 
                ? 'Reative sua Conta' 
                : 'Planos CERP Brasil NR1'}
            </h1>
          </div>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            {isLoggedIn && isTrialExpiredOrBlocked ? (
              <>
                <AlertCircle className="inline h-5 w-5 mr-2" />
                Seu período de teste expirou. Escolha um plano para continuar usando o sistema.
              </>
            ) : isLoggedIn ? (
              'Faça um upgrade no seu plano e desbloqueie mais recursos.'
            ) : (
              'Escolha o plano ideal para sua empresa e garanta conformidade com as normas de Segurança e Saúde no Trabalho'
            )}
          </p>
          
          {/* Show user info if logged in */}
          {isLoggedIn && (
            <div className="mt-4 inline-flex items-center bg-white/10 rounded-lg px-4 py-2 text-sm">
              <Users className="h-4 w-4 mr-2" />
              Logado como: <span className="font-semibold ml-1">{user.email}</span>
              {companyStatus?.status && (
                <Badge className={`ml-3 ${
                  companyStatus.status === 'active' ? 'bg-green-500' :
                  companyStatus.status === 'trial' ? 'bg-amber-500' :
                  'bg-red-500'
                }`}>
                  {companyStatus.status === 'active' ? 'Ativo' :
                   companyStatus.status === 'trial' ? 'Trial' :
                   companyStatus.status === 'expired' ? 'Expirado' :
                   companyStatus.status === 'cancelled' ? 'Cancelado' :
                   'Bloqueado'}
                </Badge>
              )}
            </div>
          )}
          
          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center bg-white/10 rounded-lg p-1">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-white text-[#1e3a5f]' 
                  : 'text-white/80 hover:text-white'
              }`}
              onClick={() => setBillingCycle('monthly')}
              data-testid="toggle-monthly"
            >
              Mensal
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                billingCycle === 'annual' 
                  ? 'bg-white text-[#1e3a5f]' 
                  : 'text-white/80 hover:text-white'
              }`}
              onClick={() => setBillingCycle('annual')}
              data-testid="toggle-annual"
            >
              Anual
              <Badge className="bg-green-500 text-white text-xs">-5%</Badge>
            </button>
          </div>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const Icon = getIcon(plan.id);
            return (
              <Card 
                key={plan.id} 
                className={`relative flex flex-col ${plan.popular ? 'border-[#0d9488] border-2 shadow-lg' : 'border-slate-200'}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#0d9488] text-white">Mais Popular</Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${plan.popular ? 'bg-[#0d9488]/10' : 'bg-slate-100'}`}>
                    <Icon className={`h-6 w-6 ${plan.popular ? 'text-[#0d9488]' : 'text-[#1e3a5f]'}`} />
                  </div>
                  <CardTitle className="text-xl text-[#1e3a5f]">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.employees_label}</CardDescription>
                </CardHeader>
                
                <CardContent className="flex-1">
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-[#1e3a5f]">
                      {formatCurrency(getMonthlyPrice(plan))}
                    </div>
                    <div className="text-sm text-slate-500">/mês</div>
                    {billingCycle === 'annual' && (
                      <div className="text-xs text-green-600 mt-1">
                        Cobrado {formatCurrency(plan.price_annual)}/ano
                      </div>
                    )}
                    {plan.multi_cnpj && (
                      <Badge variant="outline" className="mt-2 text-xs">Multi-CNPJ</Badge>
                    )}
                  </div>
                  
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-[#0d9488] mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <div className="p-4 pt-0 mt-auto">
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-[#0d9488] hover:bg-[#0f766e]' : 'bg-[#1e3a5f] hover:bg-[#15304f]'}`}
                    onClick={() => handleSelectPlan(plan)}
                    data-testid={`select-plan-${plan.id}`}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {isLoggedIn && isTrialExpiredOrBlocked 
                      ? 'Reativar Agora' 
                      : isLoggedIn 
                        ? 'Assinar Agora'
                        : 'Começar Trial Grátis'}
                  </Button>
                  {!isLoggedIn && (
                    <p className="text-xs text-center text-slate-500 mt-2">
                      7 dias grátis • Sem compromisso
                    </p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-12 text-center">
          <Card className="max-w-xl mx-auto bg-slate-50">
            <CardContent className="py-6">
              <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Precisa de ajuda?</h3>
              <p className="text-sm text-slate-600 mb-4">
                Nossa equipe está pronta para ajudar você a escolher o plano ideal.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => {
                    const phone = '5534992004667';
                    const message = encodeURIComponent('Olá! Preciso de ajuda para escolher o plano ideal do CERP Brasil.');
                    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
                  }}
                  data-testid="whatsapp-help"
                >
                  WhatsApp: (34) 99200-4667
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.location.href = 'mailto:comercial@cerpbrasil.com.br'}
                >
                  comercial@cerpbrasil.com.br
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="max-w-md" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {paymentResult ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Pagamento Criado
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5 text-[#0d9488]" />
                  Finalizar Assinatura
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedPlan?.name} - {billingCycle === 'annual' ? 'Plano Anual' : 'Plano Mensal'}
            </DialogDescription>
          </DialogHeader>
          
          {!paymentResult ? (
            <div className="space-y-4">
              {/* Price Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">Plano {selectedPlan?.name}</span>
                  <span className="font-semibold">{formatCurrency(getPrice(selectedPlan))}</span>
                </div>
                {billingCycle === 'annual' && (
                  <div className="text-xs text-green-600 mt-1">
                    Economia de 5% no plano anual
                  </div>
                )}
              </div>
              
              {/* Payment Method */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Forma de Pagamento</label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="payment-method-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PIX">
                      <div className="flex items-center gap-2">
                        <QrCode className="h-4 w-4" />
                        PIX (Aprovação instantânea)
                      </div>
                    </SelectItem>
                    <SelectItem value="BOLETO">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Boleto Bancário (1-3 dias úteis)
                      </div>
                    </SelectItem>
                    <SelectItem value="CREDIT_CARD">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Cartão de Crédito
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Submit Button */}
              <Button 
                className="w-full bg-[#0d9488] hover:bg-[#0f766e]"
                onClick={handleCheckout}
                disabled={processing}
                data-testid="confirm-checkout"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    Confirmar Pagamento
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-slate-500">
                Pagamento seguro processado via Asaas
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Payment Success */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-800">Pagamento gerado com sucesso!</p>
                    <p className="text-sm text-green-700">
                      {paymentMethod === 'PIX' && 'Escaneie o QR Code ou copie o código PIX abaixo.'}
                      {paymentMethod === 'BOLETO' && 'Acesse o boleto pelo link abaixo.'}
                      {paymentMethod === 'CREDIT_CARD' && 'Pagamento processado com sucesso.'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* PIX QR Code */}
              {paymentMethod === 'PIX' && paymentResult.pix_qr_code && (
                <div className="text-center">
                  <img 
                    src={`data:image/png;base64,${paymentResult.pix_qr_code}`}
                    alt="QR Code PIX"
                    className="mx-auto w-48 h-48 border rounded-lg"
                    data-testid="pix-qrcode"
                  />
                </div>
              )}
              
              {/* PIX Copy-Paste */}
              {paymentMethod === 'PIX' && paymentResult.pix_copy_paste && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Código PIX Copia e Cola:</label>
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      value={paymentResult.pix_copy_paste}
                      readOnly
                      className="flex-1 text-xs p-2 border rounded bg-slate-50 truncate"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(paymentResult.pix_copy_paste)}
                      data-testid="copy-pix"
                    >
                      {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Boleto Link */}
              {paymentMethod === 'BOLETO' && paymentResult.bank_slip_url && (
                <Button 
                  className="w-full"
                  onClick={() => window.open(paymentResult.bank_slip_url, '_blank')}
                  data-testid="open-boleto"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Abrir Boleto
                </Button>
              )}
              
              {/* Invoice Link */}
              {paymentResult.invoice_url && (
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open(paymentResult.invoice_url, '_blank')}
                >
                  Ver Fatura Completa
                </Button>
              )}
              
              <Button 
                variant="ghost"
                className="w-full"
                onClick={closeCheckout}
              >
                Fechar
              </Button>
              
              <p className="text-xs text-center text-slate-500">
                Após a confirmação do pagamento, seu acesso será liberado automaticamente.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="bg-slate-50 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>CERP Brasil NR1 - Gestão de Riscos Psicossociais e SST</p>
          <p className="mt-1">© 2026 Ogunhê Desenvolvimento de Sistemas Ltda</p>
          <p className="mt-1 text-xs">CNPJ: 66.298.458/0001-87</p>
        </div>
      </div>
    </div>
  );
};

export default PublicPlans;
